import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

const SiteSettingsContext = createContext({});

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    throw new Error('useSiteSettings must be used within SiteSettingsProvider');
  }
  return context;
};

const defaultSettings = {
  navigation: {
    home: true,
    portfolio: true,
    shop: true,
    services: true,
    caseStudies: true,
    about: true,
    contact: true,
  },
  socialIcons: {
    instagram: true,
    youtube: true,
    linkedin: true,
  },
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const settingsRef = doc(db, 'siteSettings', 'main');

    // Real-time listener for settings
    const unsubscribe = onSnapshot(
      settingsRef,
      async (docSnap) => {
        if (docSnap.exists()) {
          setSettings(docSnap.data());
        } else {
          // Create default settings if they don't exist
          await setDoc(settingsRef, defaultSettings);
          setSettings(defaultSettings);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching site settings:', error);
        setSettings(defaultSettings);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const updateSettings = async (newSettings) => {
    try {
      const settingsRef = doc(db, 'siteSettings', 'main');
      await setDoc(settingsRef, newSettings, { merge: true });
      return true;
    } catch (error) {
      console.error('Error updating site settings:', error);
      return false;
    }
  };

  const value = {
    settings,
    loading,
    updateSettings,
  };

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};
